import React, { useCallback, useEffect, useState } from "react";
import Card from "components/card";
import Navbar from "components/navbar";
import Loader from "components/loader/loader";
import { errorToast } from "utils/commons";
import { getAdminTutorials, getAdminTutorial } from "services/customAPI";
import { MdSchool, MdArrowBack, MdMenuBook } from "react-icons/md";
import ReactMarkdown from "react-markdown";

interface TutorialSummary {
    slug: string;
    title: string;
    sortOrder: number;
}

interface TutorialFull extends TutorialSummary {
    content: string;
}

export default function TutorialsPage() {
    const [tutorials, setTutorials] = useState<TutorialSummary[]>([]);
    const [loading, setLoading] = useState(true);
    const [selected, setSelected] = useState<TutorialFull | null>(null);
    const [loadingContent, setLoadingContent] = useState(false);

    const fetchTutorials = useCallback(async () => {
        try {
            setLoading(true);
            const res = await getAdminTutorials();
            setTutorials(res?.data ?? []);
        } catch {
            errorToast("Erro ao carregar tutoriais");
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchTutorials(); }, [fetchTutorials]);

    const openTutorial = async (slug: string) => {
        try {
            setLoadingContent(true);
            const res = await getAdminTutorial(slug);
            setSelected(res?.data ?? null);
        } catch {
            errorToast("Erro ao carregar tutorial");
        } finally {
            setLoadingContent(false);
        }
    };

    if (loading && tutorials.length === 0) return (<><Navbar flag={false} brandText="Tutoriais" /><Loader /></>);

    return (
        <>
            <Navbar flag={false} brandText="Tutoriais" />
            <Card extra="w-full mt-4 p-6 h-full">
                {/* Header */}
                <div className="flex items-center justify-between mb-6">
                    <div className="flex items-center gap-3">
                        {selected ? (
                            <button
                                onClick={() => setSelected(null)}
                                className="flex items-center gap-2 px-3 py-1.5 bg-white/10 text-gray-300 rounded-lg hover:bg-white/20 transition-all text-sm"
                            >
                                <MdArrowBack /> Voltar
                            </button>
                        ) : (
                            <MdSchool className="text-purple-400 text-2xl" />
                        )}
                        <h2 className="text-xl font-bold text-white">
                            {selected ? selected.title : "Tutoriais"}
                        </h2>
                        {!selected && (
                            <span className="text-sm text-gray-400">({tutorials.length})</span>
                        )}
                    </div>
                </div>

                {/* Tutorial content (markdown) */}
                {selected ? (
                    <div className="bg-navy-800 rounded-xl p-6 border border-white/5">
                        {loadingContent ? (
                            <Loader />
                        ) : (
                            <div className="prose prose-invert prose-sm max-w-none
                                prose-headings:text-white prose-headings:font-bold
                                prose-h1:text-2xl prose-h1:mb-4 prose-h1:mt-0
                                prose-h2:text-lg prose-h2:mb-3 prose-h2:mt-6
                                prose-h3:text-base prose-h3:mb-2 prose-h3:mt-4
                                prose-p:text-gray-300 prose-p:leading-relaxed prose-p:mb-3
                                prose-li:text-gray-300 prose-li:mb-1
                                prose-strong:text-white
                                prose-ul:pl-5 prose-ol:pl-5
                                prose-code:text-purple-300 prose-code:bg-purple-500/10 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded
                                prose-hr:border-white/10
                            ">
                                <ReactMarkdown>{selected.content}</ReactMarkdown>
                            </div>
                        )}
                    </div>
                ) : (
                    /* Tutorial list */
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                        {tutorials.map((tut, idx) => (
                            <button
                                key={tut.slug}
                                onClick={() => openTutorial(tut.slug)}
                                className="flex items-start gap-4 p-5 bg-navy-800 rounded-xl border border-white/5 hover:border-purple-500/40 hover:bg-navy-700 transition-all text-left group"
                            >
                                <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center border border-purple-500/20 group-hover:border-purple-500/40 transition-colors">
                                    <MdMenuBook className="text-purple-400 text-lg" />
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2 mb-1">
                                        <span className="text-xs text-purple-400/70 font-semibold">
                                            {String(idx + 1).padStart(2, "0")}
                                        </span>
                                        <span className="text-white font-semibold text-sm truncate">
                                            {tut.title}
                                        </span>
                                    </div>
                                    <p className="text-gray-500 text-xs">Clique para ler</p>
                                </div>
                            </button>
                        ))}
                        {tutorials.length === 0 && (
                            <p className="text-center text-gray-500 py-8 col-span-3">
                                Nenhum tutorial disponível
                            </p>
                        )}
                    </div>
                )}
            </Card>
        </>
    );
}
